"use client";

/* istanbul ignore file */
import { c as _c } from "react/compiler-runtime";
import { useContext, useRef, useState, type JSX } from "react";
import CreateWaveDrops from "./drops/CreateWaveDrops";
import CreateWavesMainSteps from "./main-steps/CreateWavesMainSteps";
import CreateWaveOverview from "./overview/CreateWaveOverview";
import CreateWaveGroups from "./groups/CreateWaveGroups";
import CreateWaveDates from "./dates/CreateWaveDates";
import CreateWaveOutcomes from "./outcomes/CreateWaveOutcomes";
import { CreateWaveStep } from "@/types/waves.types";
import CreateWaveVoting from "./voting/CreateWaveVoting";
import CreateWaveApproval from "./approval/CreateWaveApproval";
import CreateWaveActions from "./utils/CreateWaveActions";
import type { CreateWaveDescriptionHandles } from "./description/CreateWaveDescription";
import CreateWaveDescription from "./description/CreateWaveDescription";
import { getCreateNewWaveBody } from "@/helpers/waves/create-wave.helpers";
import { AuthContext } from "@/components/auth/Auth";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { ApiCreateWaveDropRequest } from "@/generated/models/ApiCreateWaveDropRequest";
import { useRouter } from "next/navigation";
import { generateDropPart } from "./services/waveMediaService";
import { getAdminGroupId } from "./services/waveGroupService";
import { useAddWaveMutation } from "./services/waveApiService";
import { useWaveConfig } from "./hooks/useWaveConfig";
import useCapacitor from "@/hooks/useCapacitor";
import CreateWaveFlow from "./CreateWaveFlow";
import { multiPartUpload } from "./services/multiPartUpload";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import { useGroupMutations } from "@/hooks/groups/useGroupMutations";
import type { ApiCreateGroup } from "@/generated/models/ApiCreateGroup";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
export default function CreateWave(t0) {
  const $ = _c(128);
  const {
    profile,
    onBack,
    onSuccess
  } = t0;
  const router = useRouter();
  const {
    isIos,
    keyboardVisible
  } = useCapacitor();
  const {
    requestAuth,
    setToast,
    connectedProfile
  } = useContext(AuthContext);
  const {
    waitAndInvalidateDrops,
    onWaveCreated,
    onGroupCreate
  } = useContext(ReactQueryWrapperContext);
  const [submitting, setSubmitting] = useState(false);
  let t1;
  if ($[0] !== onGroupCreate || $[1] !== requestAuth) {
    t1 = {
      requestAuth,
      onGroupCreate
    };
    $[0] = onGroupCreate;
    $[1] = requestAuth;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    submit: submitInlineGroup
  } = useGroupMutations(t1);
  const {
    config,
    step,
    selectedOutcomeType,
    errors,
    groupsCache,
    groupBuilders,
    setOverview,
    setDates,
    setDrops,
    setOutcomes,
    setDropsAdminCanDelete,
    onStep,
    onOutcomeTypeChange,
    onGroupSelect,
    setGroupBuilderPanel,
    setGroupBuilderRule,
    setGroupBuilderDraft,
    addGroupBuilderIdentity,
    removeGroupBuilderIdentity,
    resetGroupBuilder,
    onVotingTypeChange,
    onCategoryChange,
    onProfileIdChange,
    onTimeWeightedVotingChange,
    onThresholdChange,
    onThresholdTimeChange,
    onChatEnabledChange
  } = useWaveConfig();
  const {
    isApp
  } = useDeviceInfo();
  const createWaveDescriptionRef = useRef(null);
  let t2;
  if ($[3] !== isApp || $[4] !== onSuccess || $[5] !== onWaveCreated || $[6] !== router || $[7] !== waitAndInvalidateDrops) {
    t2 = response => {
      waitAndInvalidateDrops();
      onWaveCreated();
      onSuccess?.();
      const createdWaveRoute = getWaveRoute({
        waveId: response.id,
        isDirectMessage: false,
        isApp
      });
      if (isApp) {
        router.replace(createdWaveRoute);
      } else {
        router.push(createdWaveRoute);
      }
    };
    $[3] = isApp;
    $[4] = onSuccess;
    $[5] = onWaveCreated;
    $[6] = router;
    $[7] = waitAndInvalidateDrops;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== setToast) {
    t3 = error => {
      setToast({
        message: error as string,
        type: "error"
      });
    };
    $[9] = setToast;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setSubmitting(false);
    };
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t2 || $[13] !== t3) {
    t5 = {
      onSuccess: t2,
      onError: t3,
      onSettled: t4
    };
    $[12] = t2;
    $[13] = t3;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const addWaveMutation = useAddWaveMutation(t5);
  const [showDropError, setShowDropError] = useState(false);
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = haveDrop => {
      if (haveDrop) {
        setShowDropError(false);
      }
    };
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const onHaveDropToSubmitChange = t6;
  let t7;
  if ($[16] !== connectedProfile?.handle || $[17] !== setToast || $[18] !== submitInlineGroup) {
    t7 = async payload => {
      const result = await submitInlineGroup({
        payload,
        currentHandle: connectedProfile?.handle ?? null
      });
      if (!result.ok) {
        if (result.reason !== "auth") {
          setToast({
            message: result.error,
            type: "error"
          });
        }
        return null;
      }
      setToast({
        message: "Group created and attached.",
        type: "success"
      });
      return result.group;
    };
    $[16] = connectedProfile?.handle;
    $[17] = setToast;
    $[18] = submitInlineGroup;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const onInlineGroupCreate = t7;
  let t8;
  if ($[20] !== addWaveMutation || $[21] !== config || $[22] !== connectedProfile?.handle || $[23] !== connectedProfile?.primary_wallet || $[24] !== requestAuth || $[25] !== setToast) {
    t8 = async () => {
      setSubmitting(true);
      const {
        success
      } = await requestAuth();
      if (!success) {
        setSubmitting(false);
        return;
      }
      const drop = createWaveDescriptionRef.current?.requestDrop() ?? null;
      if (!drop?.parts.length) {
        setSubmitting(false);
        setShowDropError(true);
        return;
      }
      const adminGroupId = await getAdminGroupId({
        adminGroupId: config.groups.admin,
        primaryWallet: connectedProfile?.primary_wallet,
        handle: connectedProfile?.handle ?? undefined,
        onError: error_0 => {
          setToast({
            message: typeof error_0 === "string" ? error_0 : "Failed to get admin group",
            type: "error"
          });
        }
      });
      if (!adminGroupId) {
        setSubmitting(false);
        return;
      }
      const dropParts = await Promise.all(drop.parts.map(_temp));
      const dropRequest = {
        title: drop.title ?? null,
        parts: dropParts.map(_temp3),
        referenced_nfts: drop.referenced_nfts.map(_temp4),
        mentioned_users: drop.mentioned_users.map(_temp5),
        metadata: drop.metadata.map(_temp6),
        signature: null
      };
      const picture = config.overview.image ? await multiPartUpload({
        file: config.overview.image,
        path: "wave"
      }) : null;
      const waveBody = getCreateNewWaveBody({
        config: {
          ...config,
          groups: {
            ...config.groups,
            admin: adminGroupId
          }
        },
        picture: picture?.url ?? null,
        drop: dropRequest
      });
      await addWaveMutation.mutateAsync(waveBody);
    };
    $[20] = addWaveMutation;
    $[21] = config;
    $[22] = connectedProfile?.handle;
    $[23] = connectedProfile?.primary_wallet;
    $[24] = requestAuth;
    $[25] = setToast;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const onComplete = t8;
  let t9;
  if ($[27] !== config.overview || $[28] !== errors || $[29] !== setOverview) {
    t9 = <CreateWaveOverview overview={config.overview} errors={errors} setOverview={setOverview} />;
    $[27] = config.overview;
    $[28] = errors;
    $[29] = setOverview;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== addGroupBuilderIdentity || $[32] !== config.chat.enabled || $[33] !== config.drops.adminCanDeleteDrops || $[34] !== config.groups || $[35] !== config.overview.name || $[36] !== config.overview.type || $[37] !== groupBuilders || $[38] !== groupsCache || $[39] !== onChatEnabledChange || $[40] !== onGroupSelect || $[41] !== onInlineGroupCreate || $[42] !== removeGroupBuilderIdentity || $[43] !== resetGroupBuilder || $[44] !== setDropsAdminCanDelete || $[45] !== setGroupBuilderDraft || $[46] !== setGroupBuilderPanel || $[47] !== setGroupBuilderRule) {
    t10 = <CreateWaveGroups waveName={config.overview.name} waveType={config.overview.type} groups={config.groups} groupsCache={groupsCache} groupBuilders={groupBuilders} chatEnabled={config.chat.enabled} adminCanDeleteDrops={config.drops.adminCanDeleteDrops} setChatEnabled={onChatEnabledChange} onGroupSelect={onGroupSelect} onInlineGroupCreate={onInlineGroupCreate} setGroupBuilderPanel={setGroupBuilderPanel} setGroupBuilderRule={setGroupBuilderRule} setGroupBuilderDraft={setGroupBuilderDraft} addGroupBuilderIdentity={addGroupBuilderIdentity} removeGroupBuilderIdentity={removeGroupBuilderIdentity} resetGroupBuilder={resetGroupBuilder} setDropsAdminCanDelete={setDropsAdminCanDelete} />;
    $[31] = addGroupBuilderIdentity;
    $[32] = config.chat.enabled;
    $[33] = config.drops.adminCanDeleteDrops;
    $[34] = config.groups;
    $[35] = config.overview.name;
    $[36] = config.overview.type;
    $[37] = groupBuilders;
    $[38] = groupsCache;
    $[39] = onChatEnabledChange;
    $[40] = onGroupSelect;
    $[41] = onInlineGroupCreate;
    $[42] = removeGroupBuilderIdentity;
    $[43] = resetGroupBuilder;
    $[44] = setDropsAdminCanDelete;
    $[45] = setGroupBuilderDraft;
    $[46] = setGroupBuilderPanel;
    $[47] = setGroupBuilderRule;
    $[48] = t10;
  } else {
    t10 = $[48];
  }
  let t11;
  if ($[49] !== config.dates || $[50] !== config.overview.type || $[51] !== setDates) {
    t11 = <CreateWaveDates waveType={config.overview.type} dates={config.dates} setDates={setDates} />;
    $[49] = config.dates;
    $[50] = config.overview.type;
    $[51] = setDates;
    $[52] = t11;
  } else {
    t11 = $[52];
  }
  let t12;
  if ($[53] !== config.drops || $[54] !== config.overview.type || $[55] !== errors || $[56] !== setDrops) {
    t12 = <CreateWaveDrops waveType={config.overview.type} drops={config.drops} errors={errors} setDrops={setDrops} />;
    $[53] = config.drops;
    $[54] = config.overview.type;
    $[55] = errors;
    $[56] = setDrops;
    $[57] = t12;
  } else {
    t12 = $[57];
  }
  let t13;
  if ($[58] !== config.overview.type || $[59] !== config.voting.category || $[60] !== config.voting.profileId || $[61] !== config.voting.timeWeighted || $[62] !== config.voting.type || $[63] !== errors || $[64] !== onCategoryChange || $[65] !== onProfileIdChange || $[66] !== onTimeWeightedVotingChange || $[67] !== onVotingTypeChange) {
    t13 = <CreateWaveVoting waveType={config.overview.type} selectedType={config.voting.type} category={config.voting.category} profileId={config.voting.profileId} errors={errors} onTypeChange={onVotingTypeChange} setCategory={onCategoryChange} setProfileId={onProfileIdChange} timeWeighted={config.voting.timeWeighted} onTimeWeightedChange={onTimeWeightedVotingChange} />;
    $[58] = config.overview.type;
    $[59] = config.voting.category;
    $[60] = config.voting.profileId;
    $[61] = config.voting.timeWeighted;
    $[62] = config.voting.type;
    $[63] = errors;
    $[64] = onCategoryChange;
    $[65] = onProfileIdChange;
    $[66] = onTimeWeightedVotingChange;
    $[67] = onVotingTypeChange;
    $[68] = t13;
  } else {
    t13 = $[68];
  }
  let t14;
  if ($[69] !== config.approval.threshold || $[70] !== errors || $[71] !== onThresholdChange || $[72] !== onThresholdTimeChange) {
    t14 = <CreateWaveApproval threshold={config.approval.threshold} errors={errors} setThreshold={onThresholdChange} setThresholdTimeMs={onThresholdTimeChange} />;
    $[69] = config.approval.threshold;
    $[70] = errors;
    $[71] = onThresholdChange;
    $[72] = onThresholdTimeChange;
    $[73] = t14;
  } else {
    t14 = $[73];
  }
  let t15;
  if ($[74] !== config.dates || $[75] !== config.outcomes || $[76] !== config.overview.type || $[77] !== errors || $[78] !== onOutcomeTypeChange || $[79] !== selectedOutcomeType || $[80] !== setOutcomes) {
    t15 = <CreateWaveOutcomes outcomes={config.outcomes} outcomeType={selectedOutcomeType} waveType={config.overview.type} errors={errors} dates={config.dates} setOutcomeType={onOutcomeTypeChange} setOutcomes={setOutcomes} />;
    $[74] = config.dates;
    $[75] = config.outcomes;
    $[76] = config.overview.type;
    $[77] = errors;
    $[78] = onOutcomeTypeChange;
    $[79] = selectedOutcomeType;
    $[80] = setOutcomes;
    $[81] = t15;
  } else {
    t15 = $[81];
  }
  let t16;
  if ($[82] !== config.overview.image) {
    t16 = config.overview.image ? URL.createObjectURL(config.overview.image) : null;
    $[82] = config.overview.image;
    $[83] = t16;
  } else {
    t16 = $[83];
  }
  let t17;
  if ($[84] !== config.overview.name || $[85] !== t16) {
    t17 = {
      name: config.overview.name,
      image: t16,
      id: null
    };
    $[84] = config.overview.name;
    $[85] = t16;
    $[86] = t17;
  } else {
    t17 = $[86];
  }
  let t18;
  if ($[87] !== profile || $[88] !== showDropError || $[89] !== t17) {
    t18 = <CreateWaveDescription ref={createWaveDescriptionRef} profile={profile} showDropError={showDropError} wave={t17} onHaveDropToSubmitChange={onHaveDropToSubmitChange} />;
    $[87] = profile;
    $[88] = showDropError;
    $[89] = t17;
    $[90] = t18;
  } else {
    t18 = $[90];
  }
  let t19;
  if ($[91] !== t10 || $[92] !== t11 || $[93] !== t12 || $[94] !== t13 || $[95] !== t14 || $[96] !== t15 || $[97] !== t18 || $[98] !== t9) {
    t19 = {
      [CreateWaveStep.OVERVIEW]: t9,
      [CreateWaveStep.GROUPS]: t10,
      [CreateWaveStep.DATES]: t11,
      [CreateWaveStep.DROPS]: t12,
      [CreateWaveStep.VOTING]: t13,
      [CreateWaveStep.APPROVAL]: t14,
      [CreateWaveStep.OUTCOMES]: t15,
      [CreateWaveStep.DESCRIPTION]: t18
    };
    $[91] = t10;
    $[92] = t11;
    $[93] = t12;
    $[94] = t13;
    $[95] = t14;
    $[96] = t15;
    $[97] = t18;
    $[98] = t9;
    $[99] = t19;
  } else {
    t19 = $[99];
  }
  const stepComponent = t19;
  const t20 = `Create Wave ${config.overview.name ? `"${config.overview.name}"` : ""}`;
  let t21;
  if ($[100] !== onStep) {
    t21 = step_0 => onStep({
      step: step_0,
      direction: "backward"
    });
    $[100] = onStep;
    $[101] = t21;
  } else {
    t21 = $[101];
  }
  let t22;
  if ($[102] !== config.overview.type || $[103] !== step || $[104] !== t21) {
    t22 = <div className="tw-1/4"><CreateWavesMainSteps activeStep={step} waveType={config.overview.type} onStep={t21} /></div>;
    $[102] = config.overview.type;
    $[103] = step;
    $[104] = t21;
    $[105] = t22;
  } else {
    t22 = $[105];
  }
  const t23 = `tw-flex-1 ${isIos && !keyboardVisible ? "tw-mb-10" : ""}`;
  const t24 = stepComponent[step];
  let t25;
  if ($[106] !== t24) {
    t25 = <div className="tw-w-full tw-flex-1">{t24}</div>;
    $[106] = t24;
    $[107] = t25;
  } else {
    t25 = $[107];
  }
  let t26;
  if ($[108] !== config || $[109] !== onComplete || $[110] !== onStep || $[111] !== selectedOutcomeType || $[112] !== step || $[113] !== submitting) {
    t26 = !selectedOutcomeType && <div className="tw-mt-auto"><CreateWaveActions setStep={(step_1, direction) => onStep({
        step: step_1,
        direction
      })} step={step} config={config} submitting={submitting} onComplete={onComplete} /></div>;
    $[108] = config;
    $[109] = onComplete;
    $[110] = onStep;
    $[111] = selectedOutcomeType;
    $[112] = step;
    $[113] = submitting;
    $[114] = t26;
  } else {
    t26 = $[114];
  }
  let t27;
  if ($[115] !== t25 || $[116] !== t26) {
    t27 = <div className="tw-relative tw-w-full tw-rounded-xl tw-bg-iron-900 tw-p-4 lg:tw-p-8"><div className="tw-relative tw-h-full"><div className="tw-flex tw-h-full tw-flex-col">{t25}{t26}</div></div></div>;
    $[115] = t25;
    $[116] = t26;
    $[117] = t27;
  } else {
    t27 = $[117];
  }
  let t28;
  if ($[118] !== t23 || $[119] !== t27) {
    t28 = <div className={t23}>{t27}</div>;
    $[118] = t23;
    $[119] = t27;
    $[120] = t28;
  } else {
    t28 = $[120];
  }
  let t29;
  if ($[121] !== t22 || $[122] !== t28) {
    t29 = <div className="tw-mx-auto tw-mt-4 tw-h-full tw-w-full tw-justify-between tw-gap-x-16 lg:tw-flex xl:tw-max-w-[60rem]">{t22}{t28}</div>;
    $[121] = t22;
    $[122] = t28;
    $[123] = t29;
  } else {
    t29 = $[123];
  }
  let t30;
  if ($[124] !== onBack || $[125] !== t20 || $[126] !== t29) {
    t30 = <CreateWaveFlow title={t20} onBack={onBack}>{t29}</CreateWaveFlow>;
    $[124] = onBack;
    $[125] = t20;
    $[126] = t29;
    $[127] = t30;
  } else {
    t30 = $[127];
  }
  return t30;
}
function _temp6(meta) {
  return {
    data_key: meta.data_key,
    data_value: meta.data_value
  };
}
function _temp5(user) {
  return {
    mentioned_profile_id: user.mentioned_profile_id,
    handle_in_content: user.handle_in_content
  };
}
function _temp4(nft) {
  return {
    contract: nft.contract,
    token: nft.token,
    name: nft.name
  };
}
function _temp3(part_0) {
  return {
    content: part_0.content,
    quoted_drop: part_0.quoted_drop,
    media: part_0.media.map(_temp2)
  };
}
function _temp2(media) {
  return {
    url: media.url,
    mime_type: media.mime_type
  };
}
function _temp(part) {
  return generateDropPart(part);
}